import React, { useEffect, useState } from 'react';
import { AIExecutionScore } from '../types/evaluation';
import { EvaluationApiService } from '../services/evaluationApi';
import { Award, RefreshCw, Loader2, TrendingUp, FileText, Scale, BookOpen, Play } from 'lucide-react';

interface AIExecutionScoreCardProps {
  evaluationExecutionId: string;
  providerId: string;
  onRetry?: () => void;
}

export const AIExecutionScoreCard: React.FC<AIExecutionScoreCardProps> = ({
  evaluationExecutionId,
  providerId,
  onRetry
}) => {
  const [score, setScore] = useState<AIExecutionScore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isEnqueuing, setIsEnqueuing] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const evaluationApi = EvaluationApiService.getInstance();
  const maxPollingAttempts = 10; // 10 attempts * 3 seconds = 30 seconds
  const pollingInterval = 3000; // 3 seconds

  const loadScore = async (silent = false) => {
    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);
      const data = await evaluationApi.getAIScore(evaluationExecutionId, providerId);
      setScore(data);
      setIsPolling(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI score');
      setIsPolling(false);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const handleRetry = async () => {
    try {
      setIsRetrying(true);
      setError(null);
      console.log('Retrying with:', { evaluationExecutionId, providerId });
      const newScore = await evaluationApi.retryAIScore({
        evaluationExecutionId,
        providerId
      });
      console.log('Retry successful:', newScore);
      setScore(newScore);
      onRetry?.();
    } catch (err) {
      console.error('Retry failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to retry AI score');
    } finally {
      setIsRetrying(false);
    }
  };

  const pollForScore = async (attempts: number): Promise<void> => {
    console.log(`Polling for AI score (attempt ${attempts}/${maxPollingAttempts})...`);
    
    try {
      const data = await evaluationApi.getAIScore(evaluationExecutionId, providerId);
      
      if (data) {
        // Score is available, update the state
        console.log('AI score received:', data);
        setScore(data);
        setIsPolling(false);
      } else if (attempts < maxPollingAttempts) {
        // Score not available yet, try again
        setTimeout(() => pollForScore(attempts + 1), pollingInterval);
      } else {
        // Max attempts reached
        console.warn('AI scoring timed out after 30 seconds');
        setError('AI scoring is taking longer than expected. The score may still be processing.');
        setIsPolling(false);
      }
    } catch (_err) {
      console.log(`Poll attempt ${attempts} - score not ready yet (404 expected)`);
      // 404 or other error - continue polling if under max attempts
      if (attempts < maxPollingAttempts) {
        setTimeout(() => pollForScore(attempts + 1), pollingInterval);
      } else {
        console.warn('AI scoring timed out after 30 seconds');
        setError('AI scoring is taking longer than expected. The score may still be processing.');
        setIsPolling(false);
      }
    }
  };

  const handleEnqueue = async () => {
    try {
      setIsEnqueuing(true);
      setError(null);
      
      // Construct provider execution ID (evaluationExecutionId_providerId)
      const providerExecutionId = `${evaluationExecutionId}_${providerId}`;
      console.log('Enqueuing AI scoring for provider execution:', providerExecutionId);
      
      const result = await evaluationApi.enqueueAIScoringByProviderExecutionId(providerExecutionId);
      console.log('Enqueue result:', result);
      
      if (result.enqueuedCount === 0) {
        setError('No AI scoring requests were enqueued. The execution might already have scores or was not found.');
        setIsPolling(false);
        return;
      }
      
      // Start polling for the score
      setIsPolling(true);
      
      // Start polling after a short delay
      setTimeout(() => pollForScore(1), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enqueue AI scoring');
      setIsPolling(false);
    } finally {
      setIsEnqueuing(false);
    }
  };

  useEffect(() => {
    // Start polling immediately on mount if no score exists
    const initialLoad = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await evaluationApi.getAIScore(evaluationExecutionId, providerId);
        setScore(data);
      } catch (err) {
        // If 404, start polling
        if (err instanceof Error && err.message.includes('404')) {
          setIsPolling(true);
          setTimeout(() => pollForScore(1), pollingInterval);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load AI score');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initialLoad();
  }, [evaluationExecutionId, providerId]);

  const getScoreColor = (score: number) => {
    if (score >= 9.0) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 7.0) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 5.0) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getBreakdownColor = (value: number) => {
    if (value >= 0.85) return 'bg-green-500';
    if (value >= 0.70) return 'bg-blue-500';
    if (value >= 0.50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatBreakdown = (value: number) => `${Math.round(value * 100)}%`;

  if (isLoading || isPolling) {
    return (
      <div data-component="AIExecutionScoreCard" className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-500">
            {isPolling ? 'Waiting for AI scoring...' : 'Loading AI score...'}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-component="AIExecutionScoreCard" className="bg-white rounded-lg border border-red-200 p-4">
        <div className="text-sm text-red-600">{error}</div>
        <button
          onClick={() => loadScore()}
          className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!score) {
    return (
      <div data-component="AIExecutionScoreCard" className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">No AI score available</div>
          <button
            onClick={handleEnqueue}
            disabled={isEnqueuing}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isEnqueuing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Enqueuing...</span>
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                <span>Score with AI</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-component="AIExecutionScoreCard" className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      {/* Header with Overall Score */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-bold text-lg ${getScoreColor(score.overallScore)}`}>
            <Award className="w-5 h-5" />
            <span>{score.overallScore.toFixed(1)}/10</span>
          </div>
          <div className="text-xs text-gray-500">
            <div>AI Judge Score</div>
            <div className="font-mono">{score.judgeModel}</div>
          </div>
        </div>
        <button
          onClick={handleRetry}
          disabled={isRetrying}
          className="p-2 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Retry AI scoring"
        >
          <RefreshCw className={`w-4 h-4 text-gray-600 ${isRetrying ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Reasoning */}
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <FileText className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-gray-700">{score.reasoning}</div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="space-y-3">
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Score Breakdown</div>
        
        {/* Accuracy */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">Accuracy</span>
            </div>
            <span className="font-semibold text-gray-900">{formatBreakdown(score.breakdown.accuracy)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getBreakdownColor(score.breakdown.accuracy)}`}
              style={{ width: `${score.breakdown.accuracy * 100}%` }}
            />
          </div>
        </div>

        {/* Citation Quality */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">Citation Quality</span>
            </div>
            <span className="font-semibold text-gray-900">{formatBreakdown(score.breakdown.citationQuality)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getBreakdownColor(score.breakdown.citationQuality)}`}
              style={{ width: `${score.breakdown.citationQuality * 100}%` }}
            />
          </div>
        </div>

        {/* Completeness */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">Completeness</span>
            </div>
            <span className="font-semibold text-gray-900">{formatBreakdown(score.breakdown.completeness)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getBreakdownColor(score.breakdown.completeness)}`}
              style={{ width: `${score.breakdown.completeness * 100}%` }}
            />
          </div>
        </div>

        {/* Legal Quality */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">Legal Quality</span>
            </div>
            <span className="font-semibold text-gray-900">{formatBreakdown(score.breakdown.legalQuality)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getBreakdownColor(score.breakdown.legalQuality)}`}
              style={{ width: `${score.breakdown.legalQuality * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Scoring Cost */}
      {score.scoringCost && (
        <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
          Scoring cost: ${((score.scoringCost.inputTokens * score.scoringCost.costPerMillionInputTokens / 1_000_000) + (score.scoringCost.outputTokens * score.scoringCost.costPerMillionOutputTokens / 1_000_000)).toFixed(4)} ({score.scoringCost.inputTokens || 0} in / {score.scoringCost.outputTokens || 0} out)
        </div>
      )}

      {/* Timestamp */}
      <div className="text-xs text-gray-400">
        Scored {new Date(score.scoredAt).toLocaleString()}
      </div>
    </div>
  );
};
